import { useQueryClient } from "@tanstack/react-query";
import { Button } from "antd";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import Page from "~Common/Page";
import LoaderContainer from "~Common/Page/LoaderContainer";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import { SelectSeasonEntity } from "~components/SelectSeasonEntity/SelectSeasonEntity";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { PermissionNames } from "~enums/permissions.ts";
import { RosterStatuses } from "~enums/rosterStatuses";
import { useFetchSeason } from "~hooks/apiHooks/useFetchSeason.ts";
import { useHandlePagination } from "~hooks/useHandlePagination";
import { useNotification } from "~hooks/useNotification";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import AddPlayerToRosterModal from "~pages/Roster/components/AddPlayerToRoster/AddPlayerToRosterModal.tsx";
import { RosterTableColumns } from "~pages/Roster/enums/rosterTableColumns.ts";
import useFetchEntity from "~pages/Roster/hooks/useFetchEntity";
import { useFetchInternationalGamesAmount } from "~pages/Roster/hooks/useFetchInternationalGamesAmount.ts";
import { useRosterCaptainToggle } from "~pages/Roster/hooks/useRosterCaptainToggle.ts";
import AddPlayerToSeasonRoster from "~pages/Roster/SeasonRosterListPage/components/AddPlayerToSeasonRoster.tsx";
import {
  defaultSeasonRosterExportFieldNames,
  seasonRosterExportFields,
} from "~pages/Roster/SeasonRosterListPage/constants/exportFields.ts";
import useFetchSeasonRoster from "~pages/Roster/SeasonRosterListPage/hooks/useFetchSeasonRoster";
import useFetchSeasonRosterShort from "~pages/Roster/SeasonRosterListPage/hooks/useFetchSeasonRosterShort";
import { useGetPersonRosterInfo } from "~pages/Roster/SeasonRosterListPage/hooks/useGetPersonRosterInfo";
import { useGetSeasonRosterDataTableColumns } from "~pages/Roster/SeasonRosterListPage/hooks/useGetSeasonRosterDataTableColumns.tsx";
import { useSeasonRosterService } from "~pages/Roster/SeasonRosterListPage/hooks/useSeasonRosterService.ts";
import type { SeasonRosterDataTableRow } from "~pages/Roster/SeasonRosterListPage/types/seasonRosterDataTableRow.ts";
import type { UpsertSeasonRosterModel } from "~pages/Roster/SeasonRosterListPage/types/upsertSeasonRosterModel";
import type { SeasonResponse } from "~types/season.ts";

const SeasonRosterListPage = () => {
  const { uiConfiguration } = useSportConfiguration();
  const { entityId: entityIdParam, customerId, sport, seasonId, organizationId } = useParams();

  const [entityId, setEntityId] = useState(entityIdParam);
  const [massEditMode, setMassEditMode] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(20);

  const params: Record<string, string> = {
    include: ["entities", "seasons", "persons"].join(","),
    limit: "1000",
    offset: "0",
    nameFullLocalContains: searchText,
  };

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  const { data: entity, isLoading: isEntityLoading } = useFetchEntity(entityId);
  const { data: roster, isLoading: isRosterLoading } = useFetchSeasonRoster({
    customParams: params,
    entityId,
  });

  const { data: seasonRosterShort, isLoading: isSeasonRosterShortLoading } = useFetchSeasonRosterShort();

  const { data: season, isLoading: isSeasonLoading } = useFetchSeason(seasonId, {}, (seasonData: SeasonResponse) => {
    return seasonData.data?.[0] || {};
  });

  const { data: representing, isLoading: isIntGamesAmountPending } = useFetchInternationalGamesAmount({
    representing: entity?.representing,
    seasonId: seasonId,
    discipline: season?.discipline,
  });

  const {
    upsertSeasonRosterPlayer,
    deleteSeasonRosterPlayer,
    isDeletePending: isRosterDeleting,
    exportSeasonRoster,
    isExportPending,
    importSeasonRoster,
  } = useSeasonRosterService(entityId || "");
  const { mutateAsync: getPersonRosterInfo } = useGetPersonRosterInfo();

  const invalidateRoster = () =>
    queryClient.invalidateQueries({
      queryKey: ["seasonRoster", sport, organizationId, seasonId, entityId],
    });

  const { handleCaptainToggle, deleteCaptainRole } = useRosterCaptainToggle({
    entityId,
    seasonId,
    onSuccess: invalidateRoster,
    successMessageId: "roster.update.success",
    operationItem: ApiOperationItems.SEASON_ROSTER,
  });

  const handleUpsert = async (value: UpsertSeasonRosterModel, successMessageId?: string) => {
    if (entityId && seasonId) {
      const { isPersonInRoster, roster: existingRoster } = await getPersonRosterInfo({
        personId: value.personId,
        seasonId: seasonId,
      });
      if (!isPersonInRoster) {
        await upsertSeasonRosterPlayer(
          {
            status: RosterStatuses.Active,
            ...value,
            entityId,
            seasonId,
          },
          successMessageId,
        );
      } else if (existingRoster?.entityId === entityId) {
        await upsertSeasonRosterPlayer(
          {
            status: existingRoster.status,
            ...value,
            entityId: existingRoster?.entityId,
            seasonId,
          },
          successMessageId,
        );
      } else {
        notification.error({
          message: formatMessage({
            id: "roster.error.person.exist.in.another.roster",
          }),
          placement: "bottomRight",
        });
      }
    } else {
      throw Error("Please specify entityId and seasonID");
    }
  };

  const handleDelete = async (personId: string) => {
    await deleteSeasonRosterPlayer(personId);
  };

  const handleUpdateField = async (
    row: SeasonRosterDataTableRow & DataTableRow,
    update: Partial<Pick<UpsertSeasonRosterModel, "bib" | "position" | "isCaptain" | "status">>,
  ) => {
    if (!entityId || !seasonId) {
      return;
    }
    row.disabled = true;
    try {
      await upsertSeasonRosterPlayer(
        {
          personId: row.personId,
          entityId,
          seasonId,
          status: row.status, // DataCore doesn't allow update without status!
          ...update,
        },
        "roster.update.success",
      );
    } finally {
      row.disabled = false;
    }
  };

  // Memoized so row objects keep a stable identity across unrelated re-renders,
  // which row.disabled mutation (set while a field is saving) relies on.
  const rosterTableData = useMemo(
    () =>
      roster?.map((item) => ({
        ...item,
        caps: representing ? representing[item.personId] : "",
      })),
    [roster, representing],
  );

  const addToRoster = async (value: any) => {
    const isPersonAlreadyInRoster = rosterTableData?.some((person) => person.personId === value.personId);
    if (isPersonAlreadyInRoster) {
      notification.error({
        message: formatMessage({ id: "roster.error.person.exist" }),
        placement: "bottomRight",
      });
      return;
    }
    await handleUpsert(value, "roster.add.success");
  };

  const columns = useGetSeasonRosterDataTableColumns({
    massEditMode,
    isRosterEditable: true,
    handleUpdateField,
    onCaptainToggle: handleCaptainToggle,
  });

  const isLoading =
    isEntityLoading || isRosterLoading || isRosterDeleting || isSeasonLoading || isIntGamesAmountPending || isSeasonRosterShortLoading;

  const getNavigateUrl = (personId: string) => `/${customerId}/admin/${sport}/${organizationId}/persons/manage/${personId}`;

  return (
    <LoaderContainer loading={isEntityLoading}>
      <Page
        title={`${entity?.nameFullLocal || entity?.entityId} ${formatMessage({ id: "roster" })}`}
        onBack={() => {
          navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities`);
        }}
      >
        <AddPlayerToRosterModal open={isAddModalOpen} setOpen={setIsAddModalOpen}>
          <AddPlayerToSeasonRoster handleAdd={addToRoster} tableData={rosterTableData || []} seasonRosterShort={seasonRosterShort} />
        </AddPlayerToRosterModal>
        <DataTable<SeasonRosterDataTableRow>
          headerCustomActions={
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <SelectSeasonEntity
                allowClear={false}
                style={{ width: 200 }}
                disabled={isLoading}
                onChange={(value) => {
                  if (value?.value) {
                    setEntityId(value?.value);
                    navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities/${value?.value}/roster`);
                  }
                }}
                value={{
                  label: entity?.nameFullLocal || "",
                  value: entityId || "",
                }}
              />
              <Button
                disabled={isLoading}
                type="primary"
                icon={<i style={{ paddingRight: "5px" }} className="fas fa-images" />}
                onClick={() =>
                  navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities/${entityId}/roster/images`)
                }
              >
                {formatMessage({ id: "images.manage", defaultMessage: "Manage Images" })}
              </Button>
            </div>
          }
          handleRefresh={invalidateRoster}
          columns={columns}
          rowActions={{
            base: {
              delete: {
                hidden: () => false,
                onClick: async (row) => {
                  if (row.isCaptain && row.captainRoleId) {
                    await deleteCaptainRole(row.captainRoleId);
                  }
                  await handleDelete(row.personId);
                },
                itemName: (row) => row.person.nameFullLocal || row.personId,
                loading: isRosterDeleting,
              },
              manage: {
                onClick: async (row) => {
                  navigate(getNavigateUrl(row.personId));
                },
              },
            },
          }}
          rowKey="personId"
          rowNameKey="personId"
          dataSource={rosterTableData || []}
          searchConfig={{
            handleSearch: setSearchText,
            searchText: searchText,
          }}
          isDataLoading={isLoading}
          handleAdd={() => setIsAddModalOpen(true)}
          addButtonIntlKey="roster.add"
          onMassEditModeChangedHandler={setMassEditMode}
          massEditMode={massEditMode}
          permissionNames={[PermissionNames.SEASON_ROSTER]}
          importConfig={{
            handleImport: importSeasonRoster,
            titleIntlKey: "import.modal.title.seasonRoster",
            descriptionIntlKey: "import.modal.text.seasonRoster",
          }}
          exportConfig={{
            handleExport: exportSeasonRoster,
            defaultFields: defaultSeasonRosterExportFieldNames,
            groupedFields: seasonRosterExportFields,
            localStorageKey: "export.seasonRoster",
            loading: isExportPending,
          }}
          columnChooserLocalStorageKey={`${sport}.tables.settings.columns.season.roster`}
          columnSortConfig={{
            sorterLocalStorageKey: `${sport}.tables.settings.sort.season.roster`,
            defaultSorter: { columnKey: RosterTableColumns.Bib, order: "ascend" },
          }}
          dataTableUiConfiguration={uiConfiguration.pages.seasonRoster.listPage}
          paginationConfig={{
            currentPage,
            pageSize,
            pageSizeOptions,
            handlePaginationChange,
            offset,
            total: rosterTableData?.length,
          }}
        />
      </Page>
    </LoaderContainer>
  );
};

export default SeasonRosterListPage;
